import { useState, type ReactNode } from "react";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { Bell, Download, Eye, FileClock, Star, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import PageHeader from "@/components/common/PageHeader";
import { adminWorkspaceService } from "@/services/admin-workspace.service";
import { useAuthStore } from "@/stores/authStore";

const tabs = [
  "Inbox",
  "Saved views",
  "Scheduled reports",
  "Report history",
] as const;

export default function AdminWorkspacePage() {
  const [tab, setTab] = useState<(typeof tabs)[number]>("Inbox");
  const client = useQueryClient();
  const [summary, notifications, views, schedules, runs] = useQueries({
    queries: [
      {
        queryKey: ["workspace-summary"],
        queryFn: adminWorkspaceService.summary,
      },
      {
        queryKey: ["workspace-notifications"],
        queryFn: () => adminWorkspaceService.notifications(),
      },
      {
        queryKey: ["workspace-views"],
        queryFn: () => adminWorkspaceService.savedViews(),
      },
      {
        queryKey: ["workspace-schedules"],
        queryFn: adminWorkspaceService.reportSchedules,
      },
      {
        queryKey: ["workspace-runs"],
        queryFn: adminWorkspaceService.reportRuns,
      },
    ],
  });
  const refresh = () =>
    client.invalidateQueries({
      predicate: (query) => String(query.queryKey[0]).startsWith("workspace-"),
    });
  const stats = summary.data ?? {};

  return (
    <div className="space-y-6">
      <PageHeader
        title="Admin workspace"
        subtitle="A personal action center for notifications, reusable views, and durable operational reports."
      />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Metric label="Unread" value={stats.unread ?? 0} />
        <Metric label="Overdue tasks" value={stats.overdueTasks ?? 0} />
        <Metric label="Pending approvals" value={stats.pendingApprovals ?? 0} />
        <Metric label="Failed reports" value={stats.failedReports ?? 0} />
        <Metric label="Saved views" value={stats.savedViews ?? 0} />
      </div>
      <TabBar values={tabs} value={tab} onChange={setTab} />
      {tab === "Inbox" && (
        <InboxPanel items={notifications.data ?? []} onChanged={refresh} />
      )}
      {tab === "Saved views" && (
        <SavedViewsPanel items={views.data ?? []} onChanged={refresh} />
      )}
      {tab === "Scheduled reports" && (
        <SchedulesPanel items={schedules.data ?? []} onChanged={refresh} />
      )}
      {tab === "Report history" && (
        <ReportRunsPanel items={runs.data ?? []} onChanged={refresh} />
      )}
    </div>
  );
}

function InboxPanel({
  items,
  onChanged,
}: {
  items: any[];
  onChanged: () => void;
}) {
  const read = useMutation({
    mutationFn: adminWorkspaceService.markNotificationRead,
    onSuccess: onChanged,
  });
  const dismiss = useMutation({
    mutationFn: adminWorkspaceService.dismissNotification,
    onSuccess: onChanged,
  });
  const all = useMutation({
    mutationFn: adminWorkspaceService.markAllNotificationsRead,
    onSuccess: () => {
      onChanged();
      toast.success("Inbox marked as read");
    },
  });
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button secondary onClick={() => all.mutate()}>
          <Eye className="h-3.5 w-3.5" />
          Mark all read
        </Button>
      </div>
      {items.length ? (
        items.map((item) => (
          <Panel key={item.id}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex gap-3">
                <span
                  className={`mt-1 h-2.5 w-2.5 rounded-full ${item.readAt ? "bg-slate-200" : item.severity === "CRITICAL" ? "bg-red-500" : item.severity === "WARNING" ? "bg-amber-500" : "bg-blue-500"}`}
                />
                <div>
                  <div className="flex items-center gap-2">
                    <h3
                      className={`font-bold ${item.readAt ? "text-slate-600" : "text-slate-900"}`}
                    >
                      {item.title}
                    </h3>
                    <Badge value={item.category} />
                    <Badge value={item.severity} />
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{item.message}</p>
                  <p className="mt-2 text-xs text-slate-400">
                    {new Date(item.createdAt).toLocaleString()}
                    {item.tenant ? ` · ${item.tenant.name}` : ""}
                  </p>
                  {item.actionUrl && (
                    <a
                      href={item.actionUrl}
                      onClick={() => !item.readAt && read.mutate(item.id)}
                      className="mt-2 inline-block text-xs font-bold text-blue-700"
                    >
                      Open control surface →
                    </a>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {!item.readAt && (
                  <Button secondary onClick={() => read.mutate(item.id)}>
                    Read
                  </Button>
                )}
                <button
                  aria-label="Dismiss"
                  onClick={() => dismiss.mutate(item.id)}
                  className="rounded-lg p-2 text-slate-400 hover:bg-slate-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </Panel>
        ))
      ) : (
        <Empty text="Your admin inbox is clear." />
      )}
    </div>
  );
}

function SavedViewsPanel({
  items,
  onChanged,
}: {
  items: any[];
  onChanged: () => void;
}) {
  const currentUserId = useAuthStore((state) => state.user?.id);
  const [form, setForm] = useState({
    resourceType: "TENANTS",
    name: "",
    filters: '{"status":"active"}',
    columns: "name,email,status,lastHealthScore",
    isDefault: false,
    isShared: false,
  });
  const create = useMutation({
    mutationFn: () =>
      adminWorkspaceService.createSavedView({
        resourceType: form.resourceType,
        name: form.name,
        filters: JSON.parse(form.filters),
        columns: form.columns
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
        isDefault: form.isDefault,
        isShared: form.isShared,
      }),
    onSuccess: () => {
      onChanged();
      toast.success("Saved view created");
    },
    onError: () => toast.error("Check the filters JSON and unique view name"),
  });
  const makeDefault = useMutation({
    mutationFn: adminWorkspaceService.setDefaultSavedView,
    onSuccess: onChanged,
  });
  const remove = useMutation({
    mutationFn: adminWorkspaceService.deleteSavedView,
    onSuccess: onChanged,
  });
  return (
    <div className="space-y-4">
      <Panel>
        <h3 className="font-bold">Save a reusable operating view</h3>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Select
            label="Resource"
            value={form.resourceType}
            values={[
              "TENANTS",
              "SUBSCRIPTIONS",
              "ALERTS",
              "SUPPORT_TICKETS",
              "BILLING",
            ]}
            onChange={(value) => setForm({ ...form, resourceType: value })}
          />
          <Input
            label="View name"
            value={form.name}
            onChange={(value) => setForm({ ...form, name: value })}
          />
          <Input
            label="Columns"
            value={form.columns}
            onChange={(value) => setForm({ ...form, columns: value })}
          />
          <Input
            label="Filters JSON"
            value={form.filters}
            onChange={(value) => setForm({ ...form, filters: value })}
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-xs">
          <Check
            checked={form.isDefault}
            onChange={(value) => setForm({ ...form, isDefault: value })}
            label="Default for this resource"
          />
          <Check
            checked={form.isShared}
            onChange={(value) => setForm({ ...form, isShared: value })}
            label="Share with other admins"
          />
        </div>
        <div className="mt-4">
          <Button disabled={!form.name} onClick={() => create.mutate()}>
            Save view
          </Button>
        </div>
      </Panel>
      <div className="grid gap-3 lg:grid-cols-2">
        {items.length ? (
          items.map((item) => (
            <Panel key={item.id}>
              <div className="flex justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold">{item.name}</h3>
                    {item.isDefault && <Badge value="DEFAULT" />}
                    {item.isShared && <Badge value="SHARED" />}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {item.resourceType} · owner {item.platformUser?.email}
                  </p>
                  <code className="mt-3 block rounded-lg bg-slate-50 p-2 text-[10px] text-slate-500">
                    {JSON.stringify(item.filters)}
                  </code>
                </div>
                {item.platformUserId === currentUserId && (
                  <div className="flex h-fit gap-1">
                    {!item.isDefault && (
                      <button
                        aria-label="Make default"
                        onClick={() => makeDefault.mutate(item.id)}
                        className="p-2 text-amber-500"
                      >
                        <Star className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      aria-label="Delete view"
                      onClick={() => remove.mutate(item.id)}
                      className="p-2 text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </Panel>
          ))
        ) : (
          <Empty text="No saved views." />
        )}
      </div>
    </div>
  );
}

function SchedulesPanel({
  items,
  onChanged,
}: {
  items: any[];
  onChanged: () => void;
}) {
  const tomorrow = new Date(Date.now() + 86_400_000).toISOString().slice(0, 16);
  const [form, setForm] = useState({
    name: "",
    reportType: "TENANT_HEALTH",
    format: "CSV",
    cadence: "WEEKLY",
    recipients: "",
    nextRunAt: tomorrow,
  });
  const create = useMutation({
    mutationFn: () =>
      adminWorkspaceService.createReportSchedule({
        ...form,
        recipients: form.recipients
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
        nextRunAt: new Date(form.nextRunAt).toISOString(),
      }),
    onSuccess: () => {
      onChanged();
      toast.success("Scheduled report created");
    },
  });
  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      adminWorkspaceService.setReportScheduleStatus(id, active),
    onSuccess: onChanged,
  });
  return (
    <div className="space-y-4">
      <Panel>
        <h3 className="font-bold">Schedule a durable report</h3>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <Input
            label="Schedule name"
            value={form.name}
            onChange={(value) => setForm({ ...form, name: value })}
          />
          <Select
            label="Report"
            value={form.reportType}
            values={[
              "TENANT_HEALTH",
              "REVENUE",
              "MODULE_ADOPTION",
              "CUSTOMER_SUCCESS",
            ]}
            onChange={(value) => setForm({ ...form, reportType: value })}
          />
          <Select
            label="Format"
            value={form.format}
            values={["CSV", "JSON"]}
            onChange={(value) => setForm({ ...form, format: value })}
          />
          <Select
            label="Cadence"
            value={form.cadence}
            values={["DAILY", "WEEKLY", "MONTHLY"]}
            onChange={(value) => setForm({ ...form, cadence: value })}
          />
          <Input
            label="Recipient emails"
            value={form.recipients}
            onChange={(value) => setForm({ ...form, recipients: value })}
          />
          <Input
            label="First run local time"
            type="datetime-local"
            value={form.nextRunAt}
            onChange={(value) => setForm({ ...form, nextRunAt: value })}
          />
        </div>
        <div className="mt-4">
          <Button
            disabled={!form.name || !form.recipients}
            onClick={() => create.mutate()}
          >
            Create schedule
          </Button>
        </div>
      </Panel>
      <div className="space-y-3">
        {items.length ? (
          items.map((item) => (
            <Panel key={item.id}>
              <div className="flex flex-wrap justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <FileClock className="h-4 w-4 text-blue-600" />
                    <h3 className="font-bold">{item.name}</h3>
                    <Badge value={item.isActive ? "ACTIVE" : "PAUSED"} />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {item.reportType} · {item.format} · {item.cadence} ·{" "}
                    {item.recipients.join(", ")}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    Next {new Date(item.nextRunAt).toLocaleString()} ·{" "}
                    {item._count?.runs ?? 0} runs
                  </p>
                </div>
                <Button
                  secondary
                  onClick={() =>
                    toggle.mutate({ id: item.id, active: !item.isActive })
                  }
                >
                  {item.isActive ? "Pause" : "Resume"}
                </Button>
              </div>
            </Panel>
          ))
        ) : (
          <Empty text="No scheduled reports." />
        )}
      </div>
    </div>
  );
}

function ReportRunsPanel({
  items,
  onChanged,
}: {
  items: any[];
  onChanged: () => void;
}) {
  const [reportType, setReportType] = useState("TENANT_HEALTH");
  const [format, setFormat] = useState("CSV");
  const run = useMutation({
    mutationFn: () => adminWorkspaceService.runReport({ reportType, format }),
    onSuccess: () => {
      onChanged();
      toast.success("Report generated");
    },
  });
  return (
    <div className="space-y-4">
      <Panel>
        <div className="flex flex-wrap items-end gap-3">
          <Select
            label="On-demand report"
            value={reportType}
            values={[
              "TENANT_HEALTH",
              "REVENUE",
              "MODULE_ADOPTION",
              "CUSTOMER_SUCCESS",
            ]}
            onChange={setReportType}
          />
          <Select
            label="Format"
            value={format}
            values={["CSV", "JSON"]}
            onChange={setFormat}
          />
          <Button onClick={() => run.mutate()}>Run now</Button>
        </div>
      </Panel>
      <div className="space-y-3">
        {items.length ? (
          items.map((item) => (
            <Panel key={item.id}>
              <div className="flex flex-wrap justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold">{item.reportType}</h3>
                    <Badge value={item.status} />
                    <Badge value={item.format} />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {item.rowCount ?? 0} rows ·{" "}
                    {new Date(item.createdAt).toLocaleString()} ·{" "}
                    {item.schedule?.name || "On demand"}
                  </p>
                  {item.error && (
                    <p className="mt-2 text-xs text-red-600">{item.error}</p>
                  )}
                </div>
                {item.status === "COMPLETED" && (
                  <Button secondary onClick={() => downloadReport(item)}>
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </Button>
                )}
              </div>
            </Panel>
          ))
        ) : (
          <Empty text="No report runs." />
        )}
      </div>
    </div>
  );
}

function downloadReport(item: any) {
  const content =
    item.format === "CSV"
      ? (item.result?.csv ?? "")
      : JSON.stringify(item.result?.rows ?? [], null, 2);
  const blob = new Blob([content], {
    type: item.format === "CSV" ? "text/csv" : "application/json",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${item.reportType.toLowerCase()}-${item.id}.${item.format.toLowerCase()}`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <Panel>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className="mt-1 text-2xl font-black">{value}</p>
        </div>
        <Bell className="h-4 w-4 text-blue-600" />
      </div>
    </Panel>
  );
}
function TabBar<T extends readonly string[]>({
  values,
  value,
  onChange,
}: {
  values: T;
  value: T[number];
  onChange: (value: T[number]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-2">
      {values.map((item) => (
        <button
          key={item}
          onClick={() => onChange(item)}
          className={`rounded-xl px-4 py-2 text-xs font-bold ${value === item ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"}`}
        >
          {item}
        </button>
      ))}
    </div>
  );
}
function Panel({ children }: { children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      {children}
    </section>
  );
}
function Empty({ text }: { text: string }) {
  return (
    <Panel>
      <p className="text-sm text-slate-500">{text}</p>
    </Panel>
  );
}
function Badge({ value }: { value: string }) {
  return (
    <span className="rounded-full bg-slate-100 px-2 py-1 text-[9px] font-bold uppercase text-slate-600">
      {value}
    </span>
  );
}
function Button({
  children,
  onClick,
  disabled,
  secondary = false,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  secondary?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold disabled:opacity-50 ${secondary ? "border border-slate-200 bg-white text-slate-700" : "bg-slate-900 text-white"}`}
    >
      {children}
    </button>
  );
}
function Input({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <label>
      <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
      />
    </label>
  );
}
function Select({
  label,
  value,
  values,
  onChange,
}: {
  label: string;
  value: string;
  values: string[];
  onChange: (value: string) => void;
}) {
  return (
    <label>
      <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
      >
        {values.map((item) => (
          <option key={item}>{item}</option>
        ))}
      </select>
    </label>
  );
}
function Check({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  );
}
